import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  serverTimestamp, 
  query, 
  limit, 
  getDocs, 
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  where,
  orderBy,
  setDoc,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { ORDER_STATUSES, LINE_STATUSES } from './firebase/schema';
import { normalizeFilesForFirestore, getMockupUrl, getPersistableMockupUrl } from './utils/fileHelpers';
import { maybeSetOrderReadyToInvoiceIfAllLinesComplete } from './utils/orderStatusSync';
import { enrichCustomerDocForCreate } from './utils/customerPortal';
import {
  isSampleOrder,
  lineStatusForNewOrder,
  lineExtrasForNewOrder
} from './utils/sampleOrder';
import { activityLogInit } from './utils/lineActivityLog';
import { resolveFacilityId, DEFAULT_FACILITY_ID } from './utils/facilities';
import { spPrintBlockReasonForCustomerLines } from './utils/spLicenseStore';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCsWMQ3zNu0hJ15lh6BAs8r-Gdlu70WYxM",
  authDomain: "apparel-logistics-447003-e318e.firebaseapp.com",
  projectId: "apparel-logistics-447003-e318e",
  storageBucket: "apparel-logistics-447003-e318e.firebasestorage.app",
  messagingSenderId: "597872093441",
  appId: "1:597872093441:web:af8cde16c315b0dbff5300",
  measurementId: "G-DPPVBSH4Z6"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app, 'us-central1');

// Helper function to add a customer
const addCustomerToDb = async (customerData) => {
  try {
    const docRef = await addDoc(collection(db, 'customers'), enrichCustomerDocForCreate({
      ...customerData,
      timestamp: serverTimestamp()
    }));
    console.log('Document written with ID: ', docRef.id);
    return {
      success: true,
      id: docRef.id
    };
  } catch (error) {
    console.error('Error adding customer:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Test connection function
const testConnection = async () => {
  try {
    console.log('Testing Firestore connection...');
    const customersRef = collection(db, 'customers');
    const q = query(customersRef, limit(1));
    const querySnapshot = await getDocs(q);
    
    console.log('Connection successful!');
    console.log('Number of documents:', querySnapshot.size);
    
    return {
      success: true,
      message: 'Connected to Firestore successfully'
    };
  } catch (error) {
    console.error('Firestore connection error:', error);
    return {
      success: false,
      message: error.message,
      error: error
    };
  }
};

// Helper function to normalize line type to database format (screen, dtf, dtg, embroidery, design_only)
const normalizeLineType = (lineType) => {
  const trimmed = lineType != null ? String(lineType).trim() : '';
  if (trimmed === '') return 'screen';
  const typeStr = trimmed.toLowerCase();
  // Map display values (e.g. "Screen Print", "DTF") and db values (e.g. "screen", "dtf") to consistent db format
  if (typeStr.includes('screen')) return 'screen';
  if (typeStr.includes('embroidery')) return 'embroidery';
  if (typeStr === 'dtg') return 'dtg';
  if (typeStr === 'dtf') return 'dtf';
  if (typeStr.includes('design')) return 'design_only';
  return 'screen'; // default
};

// Helper function to add an order
const addOrderToDb = async (orderData) => {
  try {
    console.log('Received order data:', orderData);

    const providedGoods = orderData.providedGoods === true;
    const isSample = isSampleOrder(orderData);
    const orderStatus = providedGoods && !isSample ? ORDER_STATUSES.ORDERED : ORDER_STATUSES.CREATED;
    const upchargePrice = providedGoods ? 0 : ((orderData.upchargePrice === '' || orderData.upchargePrice == null || Number.isNaN(parseFloat(orderData.upchargePrice))) ? 2 : parseFloat(orderData.upchargePrice));
    const normalizedFees = Array.isArray(orderData.fees)
      ? orderData.fees
          .filter((fee) => fee && fee.type)
          .map((fee, index) => ({
            id: fee.id || `fee-${index + 1}`,
            type: String(fee.type),
            quantity: Number(fee.quantity) || 0,
            price: Number(fee.price) || 0,
            taxable: fee.taxable !== false
          }))
      : [];
    const [fee1 = {}, fee2 = {}] = normalizedFees;

    let poNumber = orderData.poNumber;
    if (poNumber == null || poNumber === '') {
      poNumber = await getNextPoNumber();
    }

    const facilityId = resolveFacilityId(orderData.facilityId || DEFAULT_FACILITY_ID);

    if (isSample && orderData.customerId) {
      const blockReason = await spPrintBlockReasonForCustomerLines(
        db,
        orderData.customerId,
        orderData.lines || []
      );
      if (blockReason) {
        return { success: false, error: blockReason };
      }
    }

    // Create main order (order ID is used as tracking ID at /track/:orderId)
    const orderRef = await addDoc(collection(db, 'orders'), {
      poNumber,
      projectManager: orderData.projectManager,
      customerId: orderData.customerId,
      customerName: orderData.customerName,
      facilityId,
      startDate: serverTimestamp(),
      dueDate: orderData.dueDate,
      status: orderStatus,
      taxExempt: orderData.taxExempt || false,
      notes: orderData.notes || '',
      createdAt: serverTimestamp(),
      readyToOrder: false,
      readyToOrderDate: null,
      printedLines: 0,
      printedQuantity: 0,
      totalLines: orderData.lines.length,
      totalQuantity: orderData.totalQuantity,
      subtotal: orderData.subtotal,
      orderTotal: orderData.orderTotal,
      cogsTotal: orderData.cogsTotal,
      cogsPercentage: orderData.cogsPercentage,
      extraFees: orderData.extraFees || 0,
      fee1Type: orderData.fee1Type || fee1.type || '',
      fee1Quantity: orderData.fee1Quantity || fee1.quantity || 0,
      fee1Price: orderData.fee1Price || fee1.price || 0,
      fee2Type: orderData.fee2Type || fee2.type || '',
      fee2Quantity: orderData.fee2Quantity || fee2.quantity || 0,
      fee2Price: orderData.fee2Price || fee2.price || 0,
      fees: normalizedFees,
      ...(orderData.discount && { discount: orderData.discount }),
      ...(orderData.shippingDetails && { shippingDetails: orderData.shippingDetails }),
      upchargePrice,
      archived: false,
      invoiced: false,
      ordered: providedGoods && !isSample,
      ...(orderData.additionalInstructions && { additionalInstructions: orderData.additionalInstructions }),
      ...(providedGoods && { providedGoods: true }),
      ...(providedGoods && !isSample && { orderedAt: serverTimestamp() }),
      ...(isSample && { sample: true })
    });

    console.log('Order created with ID:', orderRef.id);

    // Create line items
    const linePromises = orderData.lines.map(async (line, index) => {
      // Process sizes to ensure all quantities are numbers
      const processedSizes = {};
      if (line.sizes) {
        Object.entries(line.sizes).forEach(([size, quantity]) => {
          processedSizes[size] = Number(quantity) || 0;
        });
      }

      // Process ink colors
      const frontInkColors = Array.isArray(line.frontInkColors) ? line.frontInkColors.filter(color => color) : [];
      const backInkColors = Array.isArray(line.backInkColors) ? line.backInkColors.filter(color => color) : [];
      const otherInkColors = Array.isArray(line.otherInkColors) ? line.otherInkColors.filter(color => color) : [];

      // Print locations: use explicit checkboxes (printLocations) when provided; else derive from ink colors for legacy
      const rawLocs = Array.isArray(line.printLocations) ? line.printLocations.filter(Boolean) : [];
      let printLocations = rawLocs.length > 0
        ? rawLocs.map(l => String(l).toLowerCase())
        : [];
      if (printLocations.length === 0) {
        if (frontInkColors.length > 0) printLocations.push('front');
        if (backInkColors.length > 0) printLocations.push('back');
        if (otherInkColors.length > 0) printLocations.push('other');
      }

      const totalQuantity = Object.values(processedSizes).reduce((sum, qty) => sum + qty, 0);
      const pricePerPiece = Number(line.pricePerPiece) || 0;
      const cogs = Number(line.cogs) || 0;

      // Normalize files and get consistent mockup URL
      const normalizedFiles = normalizeFilesForFirestore(line.files);
      const mockupUrl = getMockupUrl(line);
      
      const dbLineType = normalizeLineType(line.type);
      const isDesignOnly = dbLineType === 'design_only';
      const lineStatus = lineStatusForNewOrder({ isDesignOnly, isSample, providedGoods });
      const lineExtras = lineExtrasForNewOrder({ isDesignOnly, isSample, providedGoods });
      const lineItem = {
        orderId: orderRef.id,
        lineNumber: index + 1,
        designName: line.designName,
        garmentId: line.garmentId,
        garmentColor: line.garmentColor,
        facilityId,
        sizes: processedSizes,
        pricePerPiece,
        cogs,
        status: lineStatus,
        frontInkColors,
        backInkColors,
        otherInkColors,
        sizeType: line.sizeType || 'unisex',
        totalQuantity,
        totalLinePrice: totalQuantity * pricePerPiece,
        totalLineCOGS: totalQuantity * cogs,
        type: dbLineType,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: 'currentUser',
        activityLog: activityLogInit('Created', { to: lineStatus }),
        printLocations,
        designFileUrl: getPersistableMockupUrl(mockupUrl), // Never save blob URLs
        files: normalizedFiles, // Use normalized files object
        isIn: false,
        isCut: lineExtras.isCut === true,
        printed: lineExtras.printed === true,
        needsReorder: false,
        ...(line.pressSetup && { pressSetup: line.pressSetup }),
        ...lineExtras
      };

      console.log('Adding line item:', lineItem);
      return addDoc(collection(db, 'orderLines'), lineItem);
    });

    const results = await Promise.all(linePromises);
    console.log('Created line items:', results.map(ref => ref.id));

    // Verify all lines were created by checking they have valid IDs
    const validLineRefs = results.filter(ref => ref && ref.id).map(ref => ref.id);
    if (validLineRefs.length !== orderData.lines.length) {
      console.error(`Line creation mismatch: expected ${orderData.lines.length}, got ${validLineRefs.length}`);
      // Don't fail the order, but log the issue
    }

    // Update order with line references
    await updateDoc(orderRef, {
      lineRefs: validLineRefs
    });

    await maybeSetOrderReadyToInvoiceIfAllLinesComplete(db, orderRef.id);

    console.log(`Order ${orderRef.id} created with ${validLineRefs.length} lines`);

    return {
      success: true,
      orderId: orderRef.id,
      linesCreated: validLineRefs.length,
      linesExpected: orderData.lines.length
    };
  } catch (error) {
    console.error('Error adding order:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Helper function to get an order and its lines
const getOrderWithLines = async (orderId) => {
  try {
    // Get order
    const orderDoc = await getDoc(doc(db, 'orders', orderId));
    if (!orderDoc.exists()) {
      throw new Error('Order not found');
    }

    // Get lines
    const linesQuery = query(
      collection(db, 'orderLines'),
      where('orderId', '==', orderId),
      orderBy('lineNumber')
    );
    const linesSnapshot = await getDocs(linesQuery);
    
    const lines = [];
    linesSnapshot.forEach(doc => {
      lines.push({ id: doc.id, ...doc.data() });
    });

    return {
      success: true,
      order: { id: orderDoc.id, ...orderDoc.data() },
      lines: lines
    };
  } catch (error) {
    console.error('Error getting order:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Helper function to get next PO / invoice number (atomic via transaction)
const getNextPoNumber = async () => {
  try {
    const counterRef = doc(db, 'counters', 'orderNumber');
    return await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      if (!counterDoc.exists()) {
        transaction.set(counterRef, { value: 1000 });
        return 1000;
      }
      const currentValue = Number(counterDoc.data().value) || 1000;
      const nextValue = currentValue + 1;
      transaction.update(counterRef, { value: nextValue });
      return nextValue;
    });
  } catch (error) {
    console.error('Error getting next PO number:', error);
    throw error;
  }
};

export { 
  db, 
  storage,
  auth,
  functions,
  addCustomerToDb, 
  testConnection, 
  addOrderToDb, 
  getOrderWithLines,
  getNextPoNumber
};
export default app;